import { useEffect, useMemo, useState } from 'react';
import Raven from 'raven-js';
import useFetch from 'framework-asset-preview-lib/internal/shared/hooks/useFetch';
import { fetchCrmObjectTypeDefinitionsForPortal } from 'framework-asset-preview-lib/internal/shared/api/crm';
import AssociationGroupContainer from './AssociationGroupContainer';
import LoadingOverlay from 'framework-asset-preview-lib/internal/shared/components/LoadingOverlay';
import ErrorMessage from 'framework-asset-preview-lib/internal/shared/components/ErrorMessage';
import useFetchAssociationsFromCRM from '../hooks/useFetchAssociationsFromCRM';
import useFetchAssociationsFromOtherSources from '../hooks/useFetchAssociationsFromOtherSources';
import AssociationGroupWithFetchedObjects from './AssociationGroupWithFetchedObjects';
import assetPreviewObserver from 'framework-asset-preview-lib/utils/assetPreviewObserver';
import { REFETCH_ASSOCIATIONS } from 'framework-asset-preview-lib/utils/constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export default function AssetsSection({
  crmObject,
  nonCrmObject
}) {
  const [hoverAssetId, setHoverAssetId] = useState(0);
  const fetchCrmObjectTypeProps = useMemo(() => ({
    fetcher: fetchCrmObjectTypeDefinitionsForPortal
  }), []);
  const {
    data: crmObjectTypeDefinitionsForPortal,
    error: errorCrmObjects,
    loading: loadingCrmObjects
  } = useFetch(fetchCrmObjectTypeProps);
  const {
    called: calledAssociations,
    data: associations,
    error: errorAssociations,
    loading: loadingAssociations
  } = useFetchAssociationsFromCRM({
    crmObject,
    crmObjectTypeDefinitionsForPortal: crmObjectTypeDefinitionsForPortal || []
  });
  const {
    called: calledAssociationsFromOtherSources,
    data: associationsFromOtherSources,
    error: errorAssociationsFromOtherSources,
    loading: loadingAssociationsFromOtherSources,
    refetch: refetchAssociations
  } = useFetchAssociationsFromOtherSources({
    crmObject,
    crmObjectTypeDefinitionsForPortal: crmObjectTypeDefinitionsForPortal || [],
    nonCrmObject
  });
  const isLoading = useMemo(() => loadingAssociations || loadingAssociationsFromOtherSources || loadingCrmObjects || !calledAssociations || !calledAssociationsFromOtherSources, [calledAssociations, calledAssociationsFromOtherSources, loadingAssociations, loadingAssociationsFromOtherSources, loadingCrmObjects]);
  const isError = useMemo(() => Boolean(errorAssociations || errorCrmObjects || errorAssociationsFromOtherSources), [errorAssociations, errorAssociationsFromOtherSources, errorCrmObjects]);
  useEffect(() => {
    assetPreviewObserver.on(REFETCH_ASSOCIATIONS, refetchAssociations);
    return () => assetPreviewObserver.off(REFETCH_ASSOCIATIONS, refetchAssociations);
  }, [refetchAssociations]);
  if (isLoading) {
    return /*#__PURE__*/_jsx("div", {
      style: {
        position: 'relative',
        minHeight: 500
      },
      children: /*#__PURE__*/_jsx(LoadingOverlay, {
        previewType: "sidepanel"
      })
    });
  }
  if (isError || !associations || !crmObjectTypeDefinitionsForPortal) {
    return /*#__PURE__*/_jsx(ErrorMessage, {
      errorType: "TOP_LEVEL_ASSETS_TAB",
      objectId: crmObject.objectId,
      objectType: crmObject.objectTypeId
    });
  }

  // Sort all associations after fetch by object type name
  const sortedAssociations = [...associations, ...(associationsFromOtherSources || [])].sort((a, b) => a.objectTypeName.localeCompare(b.objectTypeName));
  return /*#__PURE__*/_jsx(_Fragment, {
    children: sortedAssociations.map((association, index) => {
      if (!association) {
        // https://git.hubteam.com/HubSpot/MarketingOrchestration/issues/3480
        Raven.captureException(new Error('INVALID_ASSOCIATION'), {
          extra: {
            objectTypeId: crmObject.objectTypeId,
            objectId: crmObject.objectId,
            totalCrmAssociations: associations.length,
            totalNonCrmAssociations: (associationsFromOtherSources || []).length,
            totalAssociations: sortedAssociations.length,
            failedAt: index
          },
          fingerprint: ['INVALID_ASSOCIATION'],
          tags: {
            lib: 'framework-asset-preview-lib'
          }
        });
        return null;
      }
      if (association.crmDefined) {
        return /*#__PURE__*/_jsx(AssociationGroupContainer, {
          associationGroup: association,
          crmObject: crmObject,
          defaultOpen: index === 0,
          hoverAssetId: hoverAssetId,
          onHoverAsset: setHoverAssetId
        }, association.toObjectTypeId);
      }
      return /*#__PURE__*/_jsx(AssociationGroupWithFetchedObjects, {
        associationGroup: association,
        crmObject: crmObject,
        defaultOpen: index === 0,
        hoverAssetId: hoverAssetId,
        onHoverAsset: setHoverAssetId
      }, association.toObjectTypeId);
    })
  });
}